<script lang="ts">
	/**
	 * The invoices behind one subscription or one order, with the refund control on each.
	 *
	 * Shared by both admin detail screens because the panel is identical on them, and what it
	 * carries is not layout: a refund is issued **against the invoice**, never against the
	 * subscription or the order, so this is the only place on either page where money moves. Two
	 * copies of that would be two places to get the revoke rule wrong.
	 *
	 * `RefundForm` owns the rules — the mandatory note, the full/partial revoke default, the
	 * major-to-minor conversion — and is opened **per invoice**, so it always knows which one it
	 * is refunding. The modal sits over the list rather than under it: the admin is acting on a
	 * specific row, and losing sight of which one is exactly the mistake that matters here.
	 */

	import { createEventDispatcher } from 'svelte';
	import {
		Button,
		Modal,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { EmptyState, InvoiceDocumentLinks, ModalHeader, Panel } from '../../../components';
	import RefundForm from '../customers/RefundForm.svelte';
	import { formatAmount } from '../../../stores/storefront';
	import { formatCalendarDate, invoiceReference, invoiceStatusLabel } from '../../../Utilities';
	import type { AdminInvoice } from '../../../AppTypes';

	export let invoices: AdminInvoice[] = [];
	/** Heading, so each screen can say what these invoices are for. */
	export let title: string = 'Invoices';
	/** Copy for the empty case — a trial and an unpaid order are empty for different reasons. */
	export let emptyTitle: string = 'Nothing has been billed yet.';

	const dispatcher = createEventDispatcher<{ refunded: void }>();

	/** The invoice a refund is being written against, or null. */
	let refunding: AdminInvoice | null = null;

	/**
	 * Only an invoice that took money can give any back.
	 *
	 * A draft, void or unpaid invoice has nothing to refund, and offering the control on one
	 * produces a provider error at the end of a form the admin has already filled in.
	 */
	function isRefundable(invoice: AdminInvoice): boolean {
		return invoice.status === 'paid' && (invoice.amountPaid ?? 0) > 0;
	}
</script>

<Panel {title} faIcon="file-invoice">
	<svelte:fragment slot="titleRight">
		<span class="text-sm text-default">{invoices.length}</span>
	</svelte:fragment>

	{#if invoices.length === 0}
		<EmptyState compact title={emptyTitle} />
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Invoice</TableHeadCell>
				<TableHeadCell>Issued</TableHeadCell>
				<TableHeadCell>Total</TableHeadCell>
				<TableHeadCell>Status</TableHeadCell>
				<TableHeadCell>Document</TableHeadCell>
				<TableHeadCell>Refund</TableHeadCell>
			</TableHead>
			<TableBody>
				{#each invoices as invoice (invoice.uuid)}
					<TableBodyRow>
						<TableBodyCell class="dataTable">
							{invoiceReference(invoice)}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{invoice.issuedAt === null ? 'Not issued yet' : formatCalendarDate(invoice.issuedAt)}
						</TableBodyCell>
						<TableBodyCell class="dataTable tabular-nums">
							<!-- Formatted from the whole currency row, never ÷100 (§10). -->
							{$formatAmount(invoice.total, invoice.currency) ?? 'Amount unavailable'}
						</TableBodyCell>
						<TableBodyCell class="dataTable">{invoiceStatusLabel(invoice)}</TableBodyCell>
						<TableBodyCell class="dataTable">
							<!--
								Ours or theirs, and the credit notes beside it. `admin` is a **prop
								rather than a guess from the route**: the customer's endpoint resolves
								ownership through the caller, so an inferred choice 404s here — and a
								404 reads exactly like a document that was never issued.
							-->
							<InvoiceDocumentLinks {invoice} />
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if isRefundable(invoice)}
								<Button size="xs" color="alternative" on:click={() => (refunding = invoice)}>
									Refund
								</Button>
							{:else}
								<span class="text-default text-xs">—</span>
							{/if}
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<Modal open={refunding !== null} size="md" title="" on:close={() => (refunding = null)}>
		<!-- A slotted fragment has to be a direct child of the component, so the guard goes
		     inside it rather than around it. -->
		<svelte:fragment slot="header">
			{#if refunding !== null}
				<ModalHeader
					title="Refund invoice {invoiceReference(refunding)}"
					faIcon="money-bill-transfer"
					tone="danger"
				/>
			{/if}
		</svelte:fragment>

		{#if refunding !== null}
			<div data-testid="refund-panel">
				<RefundForm
					invoice={refunding}
					on:issued={() => {
						refunding = null;
						dispatcher('refunded');
					}}
					on:cancel={() => (refunding = null)}
				/>
			</div>
		{/if}
	</Modal>
</Panel>
